import {
  AUTO_UPDATE_LATEST,
  CALL_CONFIGURATION,
  DEVICE_PARAM_TYPE,
  LINES,
  PARAM_TYPE,
  POLLING_INTERVAL,
  SCHEDULE_UPDATE_CHECKS,
  UPDATE_PATH,
  UPDATE_START_TIME,
  UPDATE_STOP_TIME
} from "Constants";
import {
  ConfigurationAttribute,
  ConfigurationAttributeInput,
  Maybe,
  Param2DArrayItemInput,
  ParamArrayItemInput,
  PolicyDeploymentScope,
  SoftwareSupportUpdatePath,
  SoftwareSupportUpdatePathInput
} from "generated/graphql";
import { isEmpty } from "lodash";
import { Change, SettingsChanges } from "store/settings";
import {
  Setting,
  SettingsTree,
  SettingsValues,
  SettingValue
} from "../hooks/useProvisionedDeviceSettings";

type ChangedSetting = {
  name: string;
  value: SettingValue;
  change: Change;
};

const addOrUpdate = <T>(
  array: (T | null)[],
  item: T,
  predicate: (value: T) => boolean
) => {
  let idx = array.findIndex(el => el && predicate(el));
  if (idx === -1) {
    idx = array.length;
  }
  array[idx] = item;
};

export const convertValueToString = (
  value: SettingValue,
  type: string
): string => {
  if (typeof value === "boolean") {
    switch (type) {
      case DEVICE_PARAM_TYPE.BOOL:
        return value ? "1" : "0";
      case DEVICE_PARAM_TYPE.XBOOL:
        return value ? "True" : "False";
    }
  }

  return value.toString();
};

const modelMap: Record<string, string> = {
  G7500: "G 7500",
  StudioX50: "Studio X50",
  StudioX30: "Studio X30",
  StudioX70: "Studio X70"
};
export const getDeviceModel = (model: string) => {
  if (!model) return model;

  // strip leading "Poly " from model
  const modelWithoutPoly = model.replace(/^poly /i, "");
  return modelMap[modelWithoutPoly] || modelWithoutPoly;
};

export const settingsNameMap: Record<string, string> = {
  [SCHEDULE_UPDATE_CHECKS]: "upgrade.auto.timeFrame.enable",
  [UPDATE_START_TIME]: "upgrade.auto.timeFrame.startTime",
  [UPDATE_STOP_TIME]: "upgrade.auto.timeFrame.stopTime",
  [POLLING_INTERVAL]: "upgrade.auto.polling.interval"
};
export const getDeviceSettingName = (name: string) => {
  return settingsNameMap[name] ?? name;
};

export const getFlatSettings = (
  settings: SettingsTree
): { [key: string]: Setting } => {
  let flatSettings: { [key: string]: Setting } = {};

  Object.values(settings).forEach(settingsBySubcategory => {
    Object.values(settingsBySubcategory).forEach(settingsByCollection => {
      Object.values(settingsByCollection).forEach(settingsByGroup => {
        Object.values(settingsByGroup).forEach(settings => {
          let settingsArray = Array.isArray(settings)
            ? settings
            : Object.values(settings).flat();

          settingsArray.forEach(setting => {
            flatSettings[setting.settingName] = setting;
          });
        });
      });
    });
  });

  return flatSettings;
};

export const getChangedSettings = (
  values: SettingsValues,
  changes: SettingsChanges
): Record<string, ChangedSetting> => {
  const changedSettings: Record<string, ChangedSetting> = {};

  Object.values(changes).forEach(changesBySubcategory => {
    Object.values(changesBySubcategory).forEach(settingsByCollection => {
      Object.values(settingsByCollection).forEach(settings => {
        Object.entries(settings).forEach(([setting, change]) => {
          changedSettings[setting] = {
            name: setting,
            value: values[setting],
            change
          };
        });
      });
    });
  });

  return changedSettings;
};

export const createConfigurationAttribute = (
  name: string,
  value: string,
  paramType: string,
  index1?: number,
  index2?: number
) => {
  let configurationAttr: ConfigurationAttributeInput;

  switch (paramType) {
    case PARAM_TYPE.PARAM_ARRAY:
      if (!index1) {
        return;
      }

      configurationAttr = {
        name,
        paramArray: [
          {
            currentValue: value,
            replace1: index1
          }
        ]
      };
      break;
    case PARAM_TYPE.PARAM_2D_ARRAY:
      if (!index1 || !index2) {
        return;
      }

      configurationAttr = {
        name,
        param2DArray: [
          {
            currentValue: value,
            replace1: index1,
            replace2: index2
          }
        ]
      };
      break;
    default:
      configurationAttr = {
        name,
        currentValue: value
      };
      break;
  }

  return configurationAttr;
};

export const updateConfigurationAttribute = (
  configurationAttr: ConfigurationAttributeInput,
  value: string,
  paramType: string,
  index1?: number,
  index2?: number
) => {
  switch (paramType) {
    case PARAM_TYPE.PARAM:
      configurationAttr.currentValue = value;
      break;
    case PARAM_TYPE.PARAM_ARRAY:
      if (!index1) {
        return;
      }

      configurationAttr.paramArray = configurationAttr.paramArray || [];
      addOrUpdate<ParamArrayItemInput>(
        configurationAttr.paramArray,
        { currentValue: value, replace1: index1 },
        param => param.replace1 === index1
      );

      break;
    case PARAM_TYPE.PARAM_2D_ARRAY:
      if (!index1 || !index2) {
        return;
      }

      configurationAttr.param2DArray = configurationAttr.param2DArray || [];
      addOrUpdate<Param2DArrayItemInput>(
        configurationAttr.param2DArray,
        { currentValue: value, replace1: index1, replace2: index2 },
        param => param.replace1 === index1 && param.replace2 === index2
      );

      break;
  }
};

export const getConfigurationAttributesInput = (
  policyConfigurationAttributes: Array<ConfigurationAttribute | null>
): ConfigurationAttributeInput[] => {
  let configurationAttributes: Array<ConfigurationAttributeInput> = [];
  configurationAttributes = policyConfigurationAttributes
    .filter((attr): attr is ConfigurationAttribute => attr !== null)
    .map(attr => ({
      name: attr.name,
      currentValue: attr.currentValue,
      paramArray: attr.paramArray?.map(param => {
        // @ts-ignore
        delete param?.__typename;
        return param;
      }),
      param2DArray: attr.param2DArray?.map(param => {
        // @ts-ignore
        delete param?.__typename;
        return param;
      })
    }));

  return configurationAttributes;
};

export const isDeviceSwVersionChanged = (
  unsavedSettings: SettingsChanges,
  values: SettingsValues
) => {
  const changedSettings = getChangedSettings(values, unsavedSettings);
  return Object.values(changedSettings).some(
    changedSetting =>
      changedSetting.name === AUTO_UPDATE_LATEST ||
      changedSetting.name === UPDATE_PATH
  );
};

export const isLines = (category: string | null, subcategory: string | null) =>
  category === CALL_CONFIGURATION && subcategory === LINES;

export const getSupportUpdatePathInput = (
  supportUpdatePath: SoftwareSupportUpdatePath[]
): Maybe<SoftwareSupportUpdatePathInput[]> =>
  isEmpty(supportUpdatePath)
    ? null
    : supportUpdatePath.map(({ deviceModel, swPath }) => ({
        deviceModel,
        swPath
      }));

export const getScope = (
  deviceId?: string,
  siteId?: string,
  groupId?: string
): PolicyDeploymentScope => {
  if (deviceId) {
    return PolicyDeploymentScope.Device;
  }

  if (siteId) {
    return PolicyDeploymentScope.Site;
  }

  if (groupId) {
    return PolicyDeploymentScope.Group;
  }

  return PolicyDeploymentScope.DeviceModel;
};
